import React, { useState, useEffect, useMemo } from 'react';
import {
    Container,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    InputAdornment,
    CircularProgress,
    Pagination,
    FormControlLabel,
    Switch,
    Typography,
    Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PageHeader from '../header/page-header';
import { getPurchaseOrders } from '../../api/goodsin.api';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { LOCAL_STORAGE } from '../../constants/constants';
import { hasAccessLevel, itAccessIds, warehouseAccessIds } from '../../constants/access-ids';

const ITEMS_PER_PAGE = 10;

/**
 * Validates raw input and returns the full PO ID string if valid, otherwise null.
 *
 * newFormat = true  → "PUR-A-" + exactly 8 digits  e.g. PUR-A-00040007
 * newFormat = false → "PUR"    + exactly 6 digits  e.g. PUR990864
 *
 * The user types only the numeric portion; the prefix is appended automatically.
 * We also accept a fully-typed ID (with prefix) for convenience.
 */
const buildPoId = (raw, newFormat) => {
    const trimmed = raw.trim();
    if (!trimmed) return null;

    if (newFormat) {
        const stripped = trimmed.startsWith('PUR-A-') ? trimmed.slice(6) : trimmed;
        if (/^\d{8}$/.test(stripped)) return `PUR-A-${stripped}`;
    } else {
        const stripped = trimmed.toUpperCase().startsWith('PUR') ? trimmed.slice(3) : trimmed;
        if (/^\d{6}$/.test(stripped)) return `PUR${stripped}`;
    }
    return null;
};

const PurchaseOrders = () => {
    const navigate = useNavigate();

    const { canUpload } = useMemo(() => {
        try {
            const token = localStorage.getItem(LOCAL_STORAGE.JJ_INTERNAL);
            const groups = jwtDecode(token).groups ?? [];
            const admin = hasAccessLevel(groups, itAccessIds);
            return {
                canUpload: admin || hasAccessLevel(groups, warehouseAccessIds),
            };
        } catch {
            return { canUpload: false };
        }
    }, []);

    const [poList, setPoList]             = useState([]);
    const [loading, setLoading]           = useState(false);
    const [searchText, setSearchText]     = useState('');
    const [page, setPage]                 = useState(1);
    const [useNewFormat, setUseNewFormat] = useState(true); // true = PUR-A- (8 digits), false = PUR (6 digits)

    const validPoId    = useMemo(() => buildPoId(searchText, useNewFormat), [searchText, useNewFormat]);
    const prefix       = useNewFormat ? 'PUR-A-' : 'PUR';
    const digitLength  = useNewFormat ? 8 : 6;

    // Fetch only when we have a complete, valid PO ID
    useEffect(() => {
        if (validPoId) {
            fetchPOs(validPoId);
        } else {
            setPoList([]);
            setPage(1);
        }
    }, [validPoId]);

    useEffect(() => { setPage(1); }, [searchText, useNewFormat]);

    const handleToggleFormat = (e) => {
        setUseNewFormat(e.target.checked);
        setSearchText('');
        setPoList([]);
    };

    const fetchPOs = async (poId) => {
        setLoading(true);
        try {
            const data = await getPurchaseOrders({
                statuses: ['Received', 'Invoiced'],
                poId,
            });
            setPoList(data);
            setPage(1);
        } catch (err) {
            console.error('Failed to fetch POs:', err);
        } finally {
            setLoading(false);
        }
    };

    const totalPages = useMemo(
        () => Math.max(1, Math.ceil(poList.length / ITEMS_PER_PAGE)),
        [poList.length]
    );

    const pagedList = useMemo(() => {
        const start = (page - 1) * ITEMS_PER_PAGE;
        return poList.slice(start, start + ITEMS_PER_PAGE);
    }, [poList, page]);

    const handleRowClick = (poId) => {
        navigate(`/goodsin-admin/po/${encodeURIComponent(poId)}`, {
            state: { canUpload }
        });
    };

    // Count of numeric digits the user has entered so far (strip prefix if typed)
    const inputDigitCount = (() => {
        const trimmed = searchText.trim();
        if (useNewFormat) return (trimmed.startsWith('PUR-A-') ? trimmed.slice(6) : trimmed).length;
        return (trimmed.toUpperCase().startsWith('PUR') ? trimmed.slice(3) : trimmed).length;
    })();

    const helperText = searchText.trim() === ''
        ? `Enter ${digitLength} digits — prefix ${prefix} is added automatically`
        : validPoId
            ? `Searching for: ${validPoId}`
            : `${inputDigitCount} / ${digitLength} digits entered`;

    return (
        <Container maxWidth="xl">
            <PageHeader
                title="GRN Portal — Purchase Orders"
                onRefresh={() => validPoId && fetchPOs(validPoId)}
                refreshNeeded={true}
            />

            {/* ── FILTERS ── */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'center' }}>

                {/* Format toggle */}
                <Box sx={{ display: 'flex', alignItems: 'center', pt: 0.5 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={useNewFormat}
                                onChange={handleToggleFormat}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked':                   { color: '#1e88e5' },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#1e88e5' },
                                }}
                            />
                        }
                        label={
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e88e5' }}>
                                {useNewFormat ? 'New format (PUR-A-XXXXXXXX)' : 'Old format (PURXXXXXX)'}
                            </Typography>
                        }
                    />
                </Box>

                {/* PO ID input with static prefix badge */}
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                            label={prefix}
                            size="small"
                            sx={{
                                backgroundColor: '#1e88e5',
                                color: 'white',
                                fontWeight: 700,
                                fontSize: '0.78rem',
                                borderRadius: '4px',
                                height: 40,
                                px: 0.5,
                            }}
                        />
                        <TextField
                            size="small"
                            placeholder={'0'.repeat(digitLength)}
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                            sx={{
                                width: 200,
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset':              { borderColor: validPoId ? 'rgb(38, 174, 93)' : undefined },
                                    '&:hover fieldset':        { borderColor: validPoId ? 'rgb(38, 174, 93)' : '#1e88e5' },
                                    '&.Mui-focused fieldset':  { borderColor: validPoId ? 'rgb(38, 174, 93)' : '#1e88e5' },
                                },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" sx={{ color: validPoId ? 'rgb(38, 174, 93)' : 'action' }} />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Box>
                    <Typography
                        variant="caption"
                        sx={{
                            mt: 0.5,
                            ml: 0.5,
                            color: validPoId ? 'rgb(38, 174, 93)' : 'text.secondary',
                            fontWeight: validPoId ? 600 : 400,
                        }}
                    >
                        {helperText}
                    </Typography>
                </Box>
            </Box>

            {/* ── TABLE ── */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                    <CircularProgress sx={{ color: '#1e88e5' }} />
                </Box>
            ) : (
                <>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead className="tableTitleBar">
                                <TableRow>
                                    <TableCell className="tableTitleLabel" sx={{ whiteSpace: 'nowrap' }}>PO ID</TableCell>
                                    <TableCell className="tableTitleLabel" sx={{ whiteSpace: 'nowrap' }}>Date Received</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {!validPoId ? (
                                    <TableRow>
                                        <TableCell colSpan={2} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                            Enter a valid PO number above to search.
                                        </TableCell>
                                    </TableRow>
                                ) : pagedList.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={2} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                            No purchase order found for <strong>{validPoId}</strong>.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    pagedList.map((po) => (
                                        <TableRow
                                            key={po.poId}
                                            hover
                                            sx={{
                                                cursor: 'pointer',
                                                '&:hover': { backgroundColor: 'rgba(30, 136, 229, 0.06)' }
                                            }}
                                            onClick={() => handleRowClick(po.poId)}
                                        >
                                            <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                                                {po.poId}
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{po.dateReceived}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {poList.length > ITEMS_PER_PAGE && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(_, p) => setPage(p)}
                                color="primary"
                                siblingCount={0}
                                boundaryCount={1}
                            />
                        </Box>
                    )}
                </>
            )}
        </Container>
    );
};

export default PurchaseOrders;